/**
 * IntegrationsOverviewModal
 *
 * Süperadmin için tüm şirketlerin platform entegrasyonlarını tek düz tabloda
 * listeler (server-side arama + sayfalama). Açılır satır yok — girilen tüm
 * alanlar sütunlarda görünür.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  IconButton,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LanIcon from '@mui/icons-material/Lan';
import SuperAdminService, { PlatformIntegrationRow, BranchOption } from '../../services/SuperAdminService';

interface CompanyOption {
  id: string;
  name: string;
}

interface IntegrationsOverviewModalProps {
  open: boolean;
  onClose: () => void;
  companies: CompanyOption[];
}

const PLATFORM_LABELS: Record<string, string> = {
  yemeksepeti: 'Yemeksepeti',
  getir: 'Getir',
  migros_yemek: 'Migros Yemek',
  trendyol_yemek: 'Trendyol Yemek',
  trendyol: 'Trendyol',
  n11: 'N11',
  hepsiburada: 'Hepsiburada',
  amazon: 'Amazon',
  pazarama: 'Pazarama',
  pttavm: 'PttAVM',
};

const PLATFORM_FILTER_OPTIONS = [
  { value: '', label: 'Tüm Platformlar' },
  ...Object.entries(PLATFORM_LABELS).map(([value, label]) => ({ value, label })),
];

const cellSx = { fontWeight: 700, bgcolor: '#f5f5f5', whiteSpace: 'nowrap' as const };

const IntegrationsOverviewModal: React.FC<IntegrationsOverviewModalProps> = ({ open, onClose, companies }) => {
  const [rows, setRows] = useState<PlatformIntegrationRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [platform, setPlatform] = useState('');
  const [companyFilter, setCompanyFilter] = useState<CompanyOption | null>(null);
  const [branchFilter, setBranchFilter] = useState<BranchOption | null>(null);
  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  const [branchOptionsLoading, setBranchOptionsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setBranchFilter(null);
    setBranchOptions([]);
    if (!companyFilter) return;
    let cancelled = false;
    (async () => {
      try {
        setBranchOptionsLoading(true);
        const options = await SuperAdminService.getInstance().getCompanyBranchOptions(companyFilter.id);
        if (!cancelled) setBranchOptions(options);
      } catch {
        if (!cancelled) setBranchOptions([]);
      } finally {
        if (!cancelled) setBranchOptionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyFilter]);

  const loadIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const service = SuperAdminService.getInstance();
      const { items, totalCount: total } = await service.getIntegrationsPaged({
        page: page + 1,
        pageSize,
        search: debouncedSearch,
        platform: platform || undefined,
        companyId: companyFilter?.id,
        branchId: branchFilter?.branchId,
      });
      setRows(items);
      setTotalCount(total);
      setLoadedOnce(true);
    } catch (e: any) {
      setError(e?.message || 'Entegrasyonlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, platform, companyFilter, branchFilter]);

  useEffect(() => {
    if (open) loadIntegrations();
  }, [open, loadIntegrations]);

  const getBranchLabel = (row: PlatformIntegrationRow): string => {
    if (!row.branchId) return '—';
    return row.branchName || row.branchId;
  };

  const handleCopy = async (value: string, key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      /* ignore */
    }
  };

  const renderValueCell = (value: string | null | undefined, copyKey: string) => {
    if (!value) return <TableCell>—</TableCell>;
    return (
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {value}
          </Typography>
          <Tooltip title={copiedKey === copyKey ? 'Kopyalandı!' : 'Kopyala'}>
            <IconButton size="small" onClick={(e) => handleCopy(value, copyKey, e)}>
              <ContentCopyIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    );
  };

  const showNoConflictHint =
    loadedOnce && !loading && !!debouncedSearch.trim() && rows.length === 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <LanIcon color="primary" />
        Şube Entegrasyonları
        <Chip label={`${totalCount} kayıt`} size="small" color="primary" sx={{ ml: 1 }} />
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Kod çakışması için aratın; sonuç yoksa kod kullanılabilir. Yemeksepeti Remote Code =
          Restaurant ID sütunu, Chain Code = App Secret Key sütunu.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            variant="outlined"
            size="small"
            autoFocus
            placeholder="Kod, şirket, mağaza ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ minWidth: 280, flex: 1, maxWidth: 420 }}
          />
          <TextField
            select
            size="small"
            label="Platform"
            value={platform}
            onChange={(e) => {
              setPlatform(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            {PLATFORM_FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            size="small"
            options={companies}
            getOptionLabel={(opt) => opt.name}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={companyFilter}
            onChange={(_, newValue) => {
              setCompanyFilter(newValue);
              setPage(0);
            }}
            renderInput={(params) => <TextField {...params} label="Şirket" placeholder="Şirket seç..." />}
            sx={{ minWidth: 240 }}
            clearOnEscape
          />
          <Autocomplete
            size="small"
            options={branchOptions}
            getOptionLabel={(opt) => opt.name}
            isOptionEqualToValue={(opt, val) => opt.branchId === val.branchId}
            value={branchFilter}
            onChange={(_, newValue) => {
              setBranchFilter(newValue);
              setPage(0);
            }}
            disabled={!companyFilter}
            loading={branchOptionsLoading}
            noOptionsText={branchOptionsLoading ? 'Yükleniyor...' : 'Şube bulunamadı'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Şube"
                placeholder={companyFilter ? 'Şube seç...' : 'Önce şirket seçin'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {branchOptionsLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ minWidth: 220 }}
            clearOnEscape
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {showNoConflictHint && (
          <Alert severity="success" sx={{ mb: 2 }}>
            "{debouncedSearch}" ile eşleşen kayıt bulunamadı — bu kod kullanımda değil.
          </Alert>
        )}

        {loading && !loadedOnce ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 && !showNoConflictHint ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              Kayıtlı entegrasyon bulunamadı.
            </Typography>
          </Box>
        ) : rows.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={cellSx}>Şirket</TableCell>
                  <TableCell sx={cellSx}>Şube</TableCell>
                  <TableCell sx={cellSx}>Platform</TableCell>
                  <TableCell sx={cellSx}>Mağaza</TableCell>
                  <TableCell sx={cellSx}>Restaurant ID / Remote Code / Store ID</TableCell>
                  <TableCell sx={cellSx}>App Secret / Chain Code / XApiKey</TableCell>
                  <TableCell sx={cellSx}>Restaurant Secret / Chain ID</TableCell>
                  <TableCell sx={cellSx}>Seller / Supplier ID</TableCell>
                  <TableCell sx={cellSx}>API Key</TableCell>
                  <TableCell sx={cellSx}>API Secret</TableCell>
                  <TableCell sx={cellSx}>Durum</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{row.companyName}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{getBranchLabel(row)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        label={PLATFORM_LABELS[row.platform] || row.platform}
                        size="small"
                        variant="outlined"
                        color={row.category === 'food' ? 'warning' : 'info'}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.storeName || '—'}</TableCell>
                    {renderValueCell(row.restaurantId, `${row.id}:rid`)}
                    {renderValueCell(row.appSecretKey, `${row.id}:ask`)}
                    {renderValueCell(row.restaurantSecretKey, `${row.id}:rsk`)}
                    {renderValueCell(row.sellerId, `${row.id}:sid`)}
                    {renderValueCell(row.apiKey, `${row.id}:ak`)}
                    {renderValueCell(row.apiSecret, `${row.id}:as`)}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        label={row.isEnabled ? 'Aktif' : 'Pasif'}
                        size="small"
                        color={row.isEnabled ? 'success' : 'default'}
                        variant={row.isEnabled ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Sayfa başına:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default IntegrationsOverviewModal;
